import type { HydraContext } from "@verbleif/lib/src/composables/Api/Type/hydra.interface";
import type { NotificationHydraItem } from "@verbleif/lib/src/composables/Api/Type/Notification/notification.interface";
import type { UserNotificationHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserNotification/userNotification.interface";
import type { HydraSource } from "@verbleif/lib/src/composables/Query/hydraSource";
import type { SearchableField } from "@verbleif/lib/src/composables/Query/searchable";
import type { InboxNotificationLinkTarget } from "@verbleif/shared/src/composables/Notifications/v2/inboxNotificationLink";
import { LoopbackFilterLogicalOperatorEnum } from "@verbleif/lib/src/composables/Api/Type/LoopbackFilter/LoopbackFilterLogicalOperator.enum";
import { LoopbackFilterOperatorEnum } from "@verbleif/lib/src/composables/Api/Type/LoopbackFilter/LoopbackFilterOperator.enum";
import { provideSearchable } from "@verbleif/lib/src/composables/Query/searchable";
import { provideSortable } from "@verbleif/lib/src/composables/Query/sortable";
import { useHydraInfiniteCollection } from "@verbleif/lib/src/composables/Query/useHydraInfiniteCollection";
import { iriToId } from "@verbleif/lib/src/composables/Utils/iri.helpers";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useServerErrorHandler } from "@verbleif/shared/src/composables/Error/serverErrorHandler";
import { useAppMutation } from "@verbleif/shared/src/composables/Mutation/useHydraMutations";
import { useNotificationEndpoints } from "@verbleif/shared/src/composables/Notifications/useNotificationEndpoints";
import { parseInboxNotificationLink } from "@verbleif/shared/src/composables/Notifications/v2/inboxNotificationLink";
import {
  applyInboxScopeFilter,
  loadNotificationCollectionPage,
  markAllInboxNotificationsReadMutation,
} from "@verbleif/shared/src/composables/Notifications/v2/notifications.api";
import { useUserNotificationsLive } from "@verbleif/shared/src/composables/Notifications/v2/useUserNotificationsLive";
import { resourceKeys, useResourceScope } from "@verbleif/shared/src/composables/Query/resourceKeys";
import { useNavbarUnreadCount } from "@verbleif/shared/src/composables/useNavbarUnreadCount";
import { createInjectionState } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { toast } from "vue-sonner";

export interface NavbarNotificationItem extends HydraContext {
  title: string
  subject: string | null
  message: string | null
  sentAt: string | null
  readAt: string | null
  target: InboxNotificationLinkTarget | null
}

function fromLegacyNotification(notification: NotificationHydraItem): NavbarNotificationItem {
  // Comment notifications carry the task or report they belong to, so link by
  // whichever one is set instead of by type.
  let target: InboxNotificationLinkTarget | null = null;
  if (notification.task) {
    target = { type: "task", id: iriToId(notification.task) };
  } else if (notification.report) {
    target = { type: "report", id: iriToId(notification.report) };
  }

  return {
    "@context": notification["@context"],
    "@id": notification["@id"],
    "@type": notification["@type"],
    "id": notification.id,
    "title": notification.title,
    "subject": notification.subject ?? null,
    "message": notification.messageWeb ?? null,
    "sentAt": notification.sentAt,
    "readAt": notification.readAt,
    target,
  };
}

function fromInboxNotification(notification: UserNotificationHydraItem): NavbarNotificationItem {
  return {
    "@context": notification["@context"],
    "@id": notification["@id"],
    "@type": notification["@type"],
    "id": notification.id,
    "title": notification.title,
    "subject": null,
    "message": notification.message,
    "sentAt": notification.listedAt ?? notification.createdAt,
    "readAt": notification.readAt,
    "target": parseInboxNotificationLink(notification.link),
  };
}

const [useProvideNavbarNotifications, useNavbarNotificationsRaw] = createInjectionState(() => {
  const authStore = useAuthStore();
  const { t } = useI18n();
  const { isV2Enabled, collectionEndpoint: ENDPOINT, markAsRead: markAsReadRequest } = useNotificationEndpoints();
  const TRANSLATION_KEY = "navbar.dropdown";
  const { unreadCount, checkUnreadCount } = useNavbarUnreadCount();
  const { onError } = useServerErrorHandler();
  const scope = useResourceScope();
  const listEnabled = ref(false);
  const markAllReadMutation = useAppMutation(markAllInboxNotificationsReadMutation());

  const { refreshCountUnlessLive } = useUserNotificationsLive();

  // The inbox and the legacy notifications name their message and date fields differently.
  function searchFieldsFor(isInbox: boolean): SearchableField<Extract<keyof NavbarNotificationItem, string>>[] {
    return [
      {
        name: "title",
        operator: LoopbackFilterOperatorEnum.ILIKE,
        logicalOperator: LoopbackFilterLogicalOperatorEnum.OR,
      },
      {
        name: (isInbox ? "message" : "messageWeb") as Extract<keyof NavbarNotificationItem, string>,
        operator: LoopbackFilterOperatorEnum.LIKE,
        logicalOperator: LoopbackFilterLogicalOperatorEnum.OR,
      },
    ];
  }

  function sortFor(isInbox: boolean) {
    return { field: isInbox ? "listedAt" : "createdAt", direction: "DESC" as const };
  }

  const searchFields = searchFieldsFor(isV2Enabled.value);
  const searchable = provideSearchable<NavbarNotificationItem>(searchFields);
  const sortable = provideSortable({}, sortFor(isV2Enabled.value));
  watch(isV2Enabled, (isInbox) => {
    // The searchable reads its fields on every request, so they are swapped in place when the module toggles.
    searchFields.splice(0, searchFields.length, ...searchFieldsFor(isInbox));
    sortable.setSort(sortFor(isInbox));
  });
  const userIri = computed(() => authStore.user.value?.["@id"] ?? "");
  const source = computed((): HydraSource<NavbarNotificationItem> | null => {
    if (!userIri.value || !ENDPOINT.value) {
      return null;
    }
    const endpoint = ENDPOINT.value;
    const isInbox = isV2Enabled.value;
    const currentScope = scope.value;
    return {
      // The inbox and the legacy notifications are different collections: switching the module must not reuse the other's pages.
      key: [...resourceKeys.notifications.list(scope.value), "of", userIri.value, isInbox ? "inbox" : "legacy"],
      load: async (params, signal) => {
        params.set("active", "true");
        if (isInbox) {
          applyInboxScopeFilter(params, currentScope);
        }
        const data = await loadNotificationCollectionPage(endpoint, params, signal);
        const member = isInbox
          ? (data.member as UserNotificationHydraItem[]).map(fromInboxNotification)
          : (data.member as NotificationHydraItem[]).map(fromLegacyNotification);
        return { ...data, member };
      },
    };
  });
  const list = useHydraInfiniteCollection(source, {
    perPage: 10,
    searchable,
    sortable,
    enabled: computed(() => listEnabled.value && !!userIri.value && !!ENDPOINT.value),
  });

  const error = ref<Error | null>(null);
  const hasError = computed(() => error.value !== null || list.hasError.value);
  const hasNotifications = computed(() => list.items.value.length > 0);
  const isAllRead = computed(() => unreadCount.value === 0);

  async function markAllAsReadV2() {
    const userId = authStore.user.value?.id;
    if (!userId) {
      return;
    }

    await markAllReadMutation.mutateAsync({ userId, scope: scope.value });
  }

  async function markAllAsReadLegacy() {
    const itemsToMark = list.items.value.filter(item => !item.readAt);

    for (const item of itemsToMark) {
      await markOneAsRead(item, true);
    }
  }

  async function markAsRead() {
    if (!hasNotifications.value || isAllRead.value) {
      return;
    }

    try {
      error.value = null;

      if (isV2Enabled.value) {
        await markAllAsReadV2();
        const readAt = new Date().toISOString();
        for (const item of list.items.value) {
          if (!item.readAt) {
            list.patchItem(item["@id"], { readAt });
          }
        }
      } else {
        await markAllAsReadLegacy();
      }

      await list.refetch();
      if (isV2Enabled.value) {
        await refreshCountUnlessLive();
      } else {
        await checkUnreadCount();
      }

      toast.success(t(`${TRANSLATION_KEY}.all_marked_read`));
    } catch (err) {
      error.value = err as Error;
      onError({ error: err });
      toast.error(t(`${TRANSLATION_KEY}.mark_read_error`));
    }
  }

  async function markOneAsRead(notification: NavbarNotificationItem, hideToast = false) {
    if (!notification.id || notification.readAt) {
      return;
    }

    try {
      const readAt = new Date().toISOString();
      await markAsReadRequest(notification.id, readAt);
      list.patchItem(notification["@id"], { readAt });
      if (isV2Enabled.value) {
        await refreshCountUnlessLive();
      } else {
        await checkUnreadCount();
      }
    } catch (caught) {
      onError({ error: caught, hideToast });
    }
  }

  async function refetch() {
    listEnabled.value = true;
    return list.refetch();
  }

  onMounted(() => {
    checkUnreadCount();
  });

  return {
    ...list,
    isAllRead,
    unreadCount,
    searchable,
    refetch,
    markAsRead,
    markOneAsRead,
    checkUnreadCount,
    error,
    hasError,
    hasNotifications,
  };
});

export function useNavbarNotifications() {
  const NavbarNotifications = useNavbarNotificationsRaw();
  if (NavbarNotifications == null) {
    throw new Error("Please call `useProvideNavbarNotifications` on the appropriate parent component");
  }
  return NavbarNotifications;
}

export { useProvideNavbarNotifications };
